/**
 * Post.tsx
 *
 * Purpose: Single post renderer for the Mythbound™ session forum.
 * Created: 2026-03-11
 * Author: ralph-manager (Sonnet code loop)
 *
 * WHAT: Display an individual forum post with author, timestamp, and content.
 * WHY: Consistent styled rendering of reply posts in thread detail view.
 * HOW: Pure presentational component — receives post data as props and renders it.
 * WHERE: Used inside ThreadDetail.tsx to render the reply list.
 */

import React from 'react';
import { Box, Text, Flex } from '@chakra-ui/react';

// =============================================================================
// TYPES
// =============================================================================

/** A forum reply post belonging to a thread. */
export interface PostData {
  /** Unique post identifier (UUID string) */
  id: string;
  /** Parent thread identifier */
  thread_id: string;
  /** Owning session identifier (denormalized) */
  session_id: string;
  /** Reply body text */
  content: string;
  /** Display name of reply author */
  author: string;
  /** ISO-format UTC timestamp */
  created_at: string;
}

export interface PostProps {
  /** Post data to display */
  post: PostData;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Post Component
 *
 * WHO: Forum readers viewing a thread detail.
 * WHAT: Renders a single reply post with author, timestamp, and content.
 * WHERE: Inside ThreadDetail reply list.
 * WHY: Provides a consistent, themed card layout for each post.
 */
const Post: React.FC<PostProps> = ({ post }) => {
  /** Format ISO timestamp to a human-readable local date+time string. */
  const formattedDate = new Date(post.created_at).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <Box
      bg="#1e1e24"
      border="1px solid"
      borderColor="#2a2a35"
      borderRadius="md"
      p={4}
      mb={3}
    >
      {/* Author row */}
      <Flex justify="space-between" align="center" mb={2}>
        <Text
          fontWeight="bold"
          color="#35889A"
          fontSize="sm"
          fontFamily="'Cinzel', 'Times New Roman', serif"
        >
          {post.author}
        </Text>
        <Text fontSize="xs" color="#888" fontFamily="'Montserrat', Arial, sans-serif">
          {formattedDate}
        </Text>
      </Flex>

      {/* Post body */}
      <Text
        color="#e0e0e0"
        fontSize="sm"
        fontFamily="'Montserrat', Arial, sans-serif"
        lineHeight="1.6"
        whiteSpace="pre-wrap"
      >
        {post.content}
      </Text>
    </Box>
  );
};

export { Post };
